import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHelmet from '@/components/base/SEOHelmet';

const siteUrl = 'https://nlafv.cn';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '樱花视频',
  url: siteUrl,
  description: '唯美樱花视频素材与观赏指南平台，提供高清樱花视频素材免费下载',
  email: 'contact@nlafv.cn',
  foundingDate: '2024',
  sameAs: [
    'https://weibo.com/sakuravideo',
    'https://space.bilibili.com/sakuravideo',
  ],
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首页', item: siteUrl },
    { '@type': 'ListItem', position: 2, name: '关于我们', item: `${siteUrl}/about` },
  ],
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    formData.delete('website_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9u0jev8l97n3eas3f5g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setFormStatus('error');
        setFormMessage(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络后重试');
    }
  };

  return (
    <>
      <SEOHelmet
        title="关于我们 - 品牌介绍与联系方式"
        description="樱花视频致力于为创作者和赏花爱好者提供最优质的樱花视频素材。了解我们的品牌故事、发展历程和联系方式。"
        keywords="樱花视频,关于我们,品牌介绍,联系我们,樱花视频素材平台"
        canonicalUrl={`${siteUrl}/about`}
        ogType="website"
        schema={{ ...aboutSchema, ...breadcrumbSchema }}
      />

      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative w-full h-[280px] md:h-[360px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Minimal%20elegant%20workspace%20with%20cherry%20blossom%20branch%20in%20vase%2C%20warm%20natural%20light%2C%20clean%20aesthetic%2C%20creative%20studio%20vibe%2C%20soft%20pink%20accents%2C%20professional%20yet%20cozy%20atmosphere&width=1920&height=720&seq=about-hero-01&orientation=landscape"
            alt="关于樱花视频"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/50"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-white mb-3">关于我们</h1>
            <p className="text-sm md:text-base text-white/70 max-w-xl">了解樱花视频的品牌故事与团队使命</p>
          </div>
        </section>

        <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
        </div>

        {/* Brand Introduction */}
        <section className="max-w-screen-2xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-6 text-center">品牌介绍</h2>
            <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
              <p>
                樱花视频成立于2024年，是一个专注于樱花视频素材收集、创作与分享的平台。我们致力于为视频创作者、摄影爱好者和赏花爱好者提供最优质的樱花视频内容。
              </p>
              <p>
                我们的团队由一群热爱自然、热爱摄影的年轻人组成。每年春天，我们都会前往日本、武汉、北京等地的著名赏樱胜地，用镜头记录下樱花盛开的最美瞬间。
              </p>
              <p>
                从4K高清特写到延时摄影，从樱花飘落的慢动作到航拍全景，我们精心制作每一段视频，力求为观众呈现樱花的千姿百态。无论你是需要视频素材的创作者，还是单纯欣赏樱花之美的爱好者，樱花视频都是你最好的选择。
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mt-10">
              {[
                { value: '500+', label: '视频素材' },
                { value: '6', label: '内容分类' },
                { value: '50万+', label: '累计播放' },
                { value: '3万+', label: '社区成员' },
              ].map((stat) => (
                <div key={stat.label} className="text-center p-4 bg-background-50 rounded-lg border border-background-200/60">
                  <div className="text-xl md:text-2xl font-heading font-bold text-primary-600 mb-1">{stat.value}</div>
                  <div className="text-xs text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation Section */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">用户口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 max-w-4xl mx-auto">
              {[
                {
                  name: '摄影师小林',
                  role: '自由摄影师',
                  text: '樱花视频的素材质量非常高，4K画质清晰细腻。我在商业项目中多次使用了这里的素材，效果非常棒，客户也很满意。',
                  rating: 5,
                },
                {
                  name: '视频创作者阿杰',
                  role: 'B站UP主',
                  text: '作为一个樱花爱好者，这个网站简直是我的宝藏！每次做樱花主题的视频都会来这里找素材，分类清晰，找起来很方便。',
                  rating: 5,
                },
                {
                  name: '旅行博主小雨',
                  role: '旅行内容创作者',
                  text: '延时摄影和樱花飘落的慢动作视频特别美，用在我的旅行Vlog里增添了不少浪漫氛围。强烈推荐给所有内容创作者！',
                  rating: 5,
                },
              ].map((review, idx) => (
                <div key={idx} className="bg-background-50 rounded-lg p-5 md:p-6 border border-background-200/60">
                  <div className="flex gap-1 mb-3">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-amber-400 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">{review.text}</p>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{review.name}</p>
                    <p className="text-xs text-foreground-500">{review.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="max-w-screen-2xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-lg mx-auto">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-3 text-center">联系我们</h2>
            <p className="text-sm text-foreground-600 text-center mb-8">
              有任何问题或建议？欢迎给我们留言，我们会尽快回复
            </p>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="space-y-4"
            >
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  姓名 <span className="text-primary-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  placeholder="请输入您的姓名"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  邮箱 <span className="text-primary-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  placeholder="请输入您的邮箱"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  placeholder="请输入留言主题"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                  placeholder="请输入您的留言内容（最多500字）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
              </div>

              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="antibot"
              />

              <button
                type="submit"
                className="w-full py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                发送留言
              </button>

              {formStatus === 'success' && (
                <p className="text-sm text-center text-green-600 font-medium">{formMessage}</p>
              )}
              {formStatus === 'error' && (
                <p className="text-sm text-center text-red-500 font-medium">{formMessage}</p>
              )}
            </form>

            {/* Contact Info */}
            <div className="mt-10 pt-8 border-t border-background-200/60 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: 'ri-mail-line', label: '邮箱', value: 'contact@nlafv.cn' },
                { icon: 'ri-wechat-line', label: '微信', value: 'SakuraVideo2024' },
                { icon: 'ri-bilibili-line', label: 'B站', value: '樱花视频官方' },
              ].map((item) => (
                <div key={item.label} className="text-center">
                  <span className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
                    <i className={`${item.icon} text-lg`}></i>
                  </span>
                  <p className="text-xs text-foreground-500 mb-1">{item.label}</p>
                  <p className="text-sm font-medium text-foreground-800">{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}